namespace Solovis.UI {
  export interface BaseControlOptions {
    controlHost?: Solovis.UI.ControlHost | JQuery | HTMLElement | string;
    requiredModules?: string[];
    renderMode?: 'classic' | 'modern'
  }
  export interface BaseControlInfo {
    name: string;
    label: string;
    parameterType: string;
    list?: boolean;
    help?: string;
    settings?: any;
    validation?: any;
    preserveValue?: any;
  }

  export class BaseControl {
    public state: number;
    public options: BaseControlOptions;
    public info: BaseControlInfo;
    public transition: any;
    public controlHost: Solovis.UI.ControlHost;
    public container: any;
    public _requiredModules: string[];

    public _initiallyHiddenContainer: boolean;
    public static hasNewRender: boolean = false;
    
    public static DEFAULT_RENDER_MODE = 'classic' as Solovis.UI.BaseControlOptions['renderMode']
    /**
     * If doInitAsync on derived class returns Promise and that returns an error, this is where that error will be stored.
     */
    public _doInitAsyncError: any;

    constructor(options: BaseControlOptions, info?: BaseControlInfo) {
      this.state = Solovis.UI.BaseControl.ControlState.uninitialized;
      this.options = options;
      this.info = info;

      // NOTE: Controls may already be performing the same check but this is a sanity check
      const modernControlsSupported = Solovis.UX.Utils.areNewControlsSupported();
      this.options.renderMode = modernControlsSupported 
        ? (options.renderMode ? options.renderMode : Solovis.UI.BaseControl.DEFAULT_RENDER_MODE) 
        : Solovis.UI.BaseControl.DEFAULT_RENDER_MODE
      
      this._setControlHost(options.controlHost ? options.controlHost : null);

      this.transition = (Solovis as any).UI.ControlHost.Transitions.Default;

      // Determine set of required modules.
      // TODO: Figure out how to get the requiredModules off of the base type.

      if (
        this.options.requiredModules ||
        (this.constructor && (this.constructor as any).requiredModules)
      ) {
        var requiredModules = [];
        if (this.options.requiredModules) {
          requiredModules = requiredModules.concat(
            this.options.requiredModules
          );
        }
        if (this.constructor && (this.constructor as any).requiredModules) {
          requiredModules = requiredModules.concat(
            (this.constructor as any).requiredModules
          );
        }

        this._requiredModules = requiredModules;
      }
    }

    public _setControlHost(controlHost) {
      if (controlHost) {
        if (controlHost instanceof Solovis.UI.ControlHost) {
          this.controlHost = controlHost;
        } else {
          // Treat controlHost as container div.
          var container = controlHost;
          var containerElement = $(container).get(0);
          if ((containerElement as any).__controlHost) {
            this.controlHost = (containerElement as any).__controlHost;
          } else {
            this.controlHost = new Solovis.UI.ControlHost({
              container: containerElement,
            });
          }
        }
      } else {
        this.controlHost = null;
      }
    }

    public _invokeInit() {
      if (this.state != Solovis.UI.BaseControl.ControlState.uninitialized) {
        // We can only call this on controls that haven't been initialized yet.
        return;
      }

      var self = this;
      var selfAsAny: any = this;
      this.state = Solovis.UI.BaseControl.ControlState.beforeInit;

      if (selfAsAny.doInitAsync || selfAsAny._requiredModules) {
        let callDoInitAsync = () => {
          let doInitAsyncResult = selfAsAny.doInitAsync();
          if (doInitAsyncResult) {
            // doInitAsync returned a value. This must be a promise and we should call notifyInitComplete on behalf of the method.
            if (!doInitAsyncResult.then) {
              console.log(
                "ERROR: BaseControl._invokeInit: doInitAsync returned value that does not appear to be a Promise."
              );
            }

            doInitAsyncResult
              .then((result) => {
                self.notifyInitComplete();
              })
              .catch((error) => {
                self._doInitAsyncError = error;
                self.notifyInitComplete();
              });
          }
        };

        if (selfAsAny._requiredModules) {
          Solovis.ModuleLoader.loadModuleListByName(
            selfAsAny._requiredModules,
            function () {
              if (selfAsAny.doInitAsync) {
                callDoInitAsync();
              } else {
                if (selfAsAny.doInit) {
                  selfAsAny.doInit();
                }

                // No initialization necessary.
                self.notifyInitComplete();
              }
            }
          );
        } else {
          callDoInitAsync();
        }

        return false;
      } else if (selfAsAny.doInit) {
        selfAsAny.doInit();
        this.notifyInitComplete();
        return true;
      } else {
        // No initialization necessary.
        this.notifyInitComplete();
        return true;
      }
    }

    public notifyInitComplete() {
      this.state = Solovis.UI.BaseControl.ControlState.afterInit;
      $(this).trigger("initComplete");
    }

    public _invokeRender() {
      if (this.state != Solovis.UI.BaseControl.ControlState.afterInit) {
        // We can only call this on controls that are done with initialization.
        return;
      }

      var selfAsAny: any = this;

      this.state = Solovis.UI.BaseControl.ControlState.beforeRender;

      if (this._doInitAsyncError) {
        this.container = this.controlHost._createControlContainer();

        let errorControl = new Solovis.UI.ErrorControl({
          controlHost: this.container,
          error: this._doInitAsyncError,
        });
        errorControl.setAsCurrentControl();

        this.notifyRenderComplete();
      } else if (selfAsAny.doRender) {
        this.container = this.controlHost._createControlContainer();
        if (this._initiallyHiddenContainer) {
          $(this.container).css("display", "none");
        } else {
          if (this.transition) {
            this.transition.beforeRender(this);
          }
        }

        selfAsAny.doRender();
        this.notifyRenderComplete();

        if (!this._initiallyHiddenContainer) {
          if (this.transition) {
            this.transition.afterRender(this);
          }
        }
      } else {
        // No render necessary.
        this.notifyRenderComplete();
        return true;
      }
    }

    public notifyRenderComplete() {
      this.state = Solovis.UI.BaseControl.ControlState.afterRender;
      $(this).trigger("renderComplete");
    }

    public _invokeDestroy() {
      if (this.state >= Solovis.UI.BaseControl.ControlState.beforeDestroy) {
        // We have already started the sequence of events to destroy the control.
        return;
      }

      var self = this;
      var selfAsAny: any = this;
      this.state = Solovis.UI.BaseControl.ControlState.beforeDestroy;

      var callDoDestroy = function () {
        if (selfAsAny.doDestroy) {
          selfAsAny.doDestroy();
        }

        if (self.container) {
          self.container.remove();
        }

        self.notifyDestroyComplete();
      };

      if (this.container && this.transition) {
        this.transition.beforeDestroy(this, callDoDestroy);
      } else {
        callDoDestroy();
      }
    }

    public notifyDestroyComplete() {
      this.state = Solovis.UI.BaseControl.ControlState.afterDestroy;
      $(this).trigger("destroyComplete");
    }

    /**
     * Set control as the current control on currently set control host.
     */
    public setAsCurrentControl() {
      this.controlHost.setCurrentControl(this);
    }

    /**
     * Binds an event handler that will automatically unregister itself when the control is destroyed.
     * @param eventSource
     * @param eventName
     * @param eventHandler
     */
    public bindEventUntilControlDestroyed(
      eventSource,
      eventName,
      eventHandler
    ) {
      var self = this;

      var wrappedHandler = function () {
        if (self.state >= Solovis.UI.BaseControl.ControlState.beforeDestroy) {
          $(eventSource).unbind(eventName, wrappedHandler);
        } else {
          eventHandler.apply(this, arguments);
        }
      };

      $(eventSource).bind(eventName, wrappedHandler);

      return wrappedHandler;
    }

    /**
     * Binds an event handler that will automatically unregister itself when the control is destroyed or the control is removed from the DOM
     * @param eventSource
     * @param eventName
     * @param eventHandler
     */
    public bindEventUntilControlDestroyedOrNoLongerPartOfDom(
      eventSource,
      eventName,
      eventHandler
    ) {
      var self = this;

      var wrappedHandler = function () {
        if (
          self.state >= Solovis.UI.BaseControl.ControlState.beforeDestroy ||
          !Solovis.UI.DomHelper.isDescendantOf(
            self.controlHost.container,
            document.body
          )
        ) {
          $(eventSource).unbind(eventName, wrappedHandler);
        } else {
          eventHandler.apply(this, arguments);
        }
      };

      $(eventSource).bind(eventName, wrappedHandler);

      return wrappedHandler;
    }

    /**
     * If control has been rendered, call callback immediately; otherwise, call callback after doRender is called.
     * @param callback
     */
    public callAfterRender(callback: () => void) {
      if (this.state < BaseControl.ControlState.afterRender) {
        $(this).one("renderComplete", callback);
      } else {
        callback();
      }
    }

    public hasBeenRemovedFromDom(): boolean {
      let notPartOfDom = !Solovis.UI.DomHelper.isDescendantOf(
        this.controlHost.container,
        document.body
      );
      let destroyed =
        this.state >= Solovis.UI.BaseControl.ControlState.beforeDestroy;

      return notPartOfDom || destroyed;
    }
  }
}

namespace Solovis.UI.BaseControl {
  /**
   * ControlStates keeps track of the current state of the control in the container.
   */
  export enum ControlState {
    uninitialized = 1,
    beforeInit = 2,
    afterInit = 3,
    beforeRender = 4,
    afterRender = 5,
    beforeDestroy = 6,
    afterDestroy = 7,
  }
}
